import React, {useState, useEffect} from "react";
import useAxios from "../../utils/useAxios";
import {useTranslation} from "react-i18next";
import FeatherIcon from "feather-icons-react";
import ErrorMessage from "../../components/ErrorMessage";
import {toast} from "react-toastify";
import SelectInput from "../../components/SelectInput";
import {COUNTRY_OPTIONS, LEVEL_OPTIONS} from "../../constants";
import FormImageUpload from "../../components/CustomModel/FormImageUpload";

const initialData = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    gender: "",
    birthday: "",
    level: "",
    country: "",
    city: "",
    resident: "",
    info: "",
};

const PlayerForm = ({playerId = null, baseId = null, onSuccess, onClose, img, width, height, canWrite, hideDelete = false}) => {
    const {t} = useTranslation();
    const api = useAxios();

    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});
    const [file, setFile] = useState(img);

    useEffect(() => {
        if (playerId) {
            api.get(`/api/v1.0/players/${playerId}/`)
                .then(res => {
                    setFormData({
                        first_name: res.data.first_name || "",
                        last_name: res.data.last_name || "",
                        email: res.data.email || "",
                        phone: res.data.phone || "",
                        gender: res.data.gender ?? "",
                        birthday: res.data.birthday || "",
                        level: res.data.level || "",
                        country: res.data.country || "",
                        city: res.data.city || "",
                        resident: res.data.resident || "",
                        info: res.data.info || "",
                    });

                    if (res.data.image) setFile(res.data.image);
                })
                .catch(console.error);
        } else {
            setFormData(initialData);
            setFile(null);
        }
    }, [playerId]);

    const handleChange = e => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        try {
            const data = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                if (value !== "" && value !== null) {
                    data.append(key, value);
                }
            });

            if (baseId) {
                data.append("base", baseId);
            }
            // console.log("file:", file);

            if (file instanceof File || (file?.file instanceof File)) {
                data.append("image", file.file || file);
                // console.log("File added to FormData:", file);
            }

            const config = {
                headers: {"Content-Type": "multipart/form-data"},
            };

            const response = playerId
                ? await api.patch(`/api/v1.0/players/${playerId}/`, data, config)
                : await api.post(`/api/v1.0/players/`, data, config);

            onSuccess?.(response.data);
            toast.success(t(playerId ? "Data updated successfully" : "Player created successfully"));
            onClose?.();
        } catch (error) {
            console.error("Player save error", error);
            setErrors(error.response?.data || {});
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(t("Are you sure you want to delete this player?"));
        if (!confirmed) return;

        try {
            await api.delete(`/api/v1.0/players/${playerId}/`);
            // toast.success(t("Player deleted successfully"));
            onSuccess?.();
            onClose?.();
        } catch (error) {
            console.error("Delete error", error);
            toast.error(t("Error deleting player"));
        }
    };

    const GENDER_OPTIONS = [
        {id: 1, name: "Player Male"},
        {id: 2, name: "Player Female"},
    ];

    return (
        <form onSubmit={handleSubmit}>
            <h2>
                <FeatherIcon icon="user"/>
                {playerId ? canWrite ? t("Edit player") : t("View player") : t("Create player")}
            </h2>

            {[
                ["last_name", "Last name"],
                ["first_name", "First name"],
            ].map(([name, label]) => (
                <div className="form-group" key={name}>
                    <label>{t(label)}</label>
                    <input
                        type="text"
                        name={name}
                        value={formData[name]}
                        onChange={handleChange}
                        className="form-control"
                        disabled={!canWrite}
                    />
                    <ErrorMessage error={errors[name] && t(errors[name])}/>
                </div>
            ))}

            <SelectInput
                data={COUNTRY_OPTIONS}
                default_text={t("Choose country")}
                label_text={t("Country")}
                input_name="country"
                default_value={formData.country}
                requiredField={false}
                setInputValue={(value) =>
                    setFormData(prev => ({...prev, country: value}))
                }
                canWrite={canWrite}
                search={true}
            />
            <ErrorMessage error={errors.country && t(errors.country)}/>
            {/* Info */}
            <div className="form-group">
                <label>{t("City")}</label>
                <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="form-control"
                    disabled={!canWrite}
                />
                <ErrorMessage error={errors.city && t(errors.city)}/>
            </div>

            {/* Level */}
            <div className="form-group">
                <SelectInput
                    data={LEVEL_OPTIONS}
                    default_text={t("Choose player category")}
                    label_text={t("Category")}
                    input_name="level"
                    default_value={formData.level}
                    requiredField={false}
                    setInputValue={(value) =>
                        setFormData(prev => ({...prev, level: value}))
                    }
                    canWrite={canWrite}
                />
                <ErrorMessage error={errors.level && t(errors.level)}/>
            </div>

            {/* Info */}
            <div className="form-group">
                <label>{t("Level")}</label>
                <input
                    type="text"
                    name="info"
                    value={formData.info}
                    onChange={handleChange}
                    className="form-control"
                    disabled={!canWrite}
                />
                <ErrorMessage error={errors.info && t(errors.info)}/>
            </div>

            {/* Gender */}
            <div className="form-group">
                <SelectInput
                    data={GENDER_OPTIONS}
                    default_text={t("Choose player gender")}
                    label_text={t("Player gender")}
                    input_name="gender"
                    default_value={formData.gender}
                    requiredField={false}
                    setInputValue={(value) =>
                        setFormData(prev => ({...prev, gender: value}))
                    }
                    canWrite={canWrite}
                />
                <ErrorMessage error={errors.gender && t(errors.gender)}/>
            </div>

            {/* Birthday */}
            <div className="form-group">
                <label>{t("Birthday")}</label>
                <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleChange}
                    className="form-control"
                    disabled={!canWrite}
                    onClick={e => e.target.showPicker?.()}
                />
                <ErrorMessage error={errors.birthday && t(errors.birthday)}/>
            </div>

            {/* Image Upload */}
            <div className="form-group form-image">

                <FormImageUpload
                    path="players"
                    id={playerId}
                    file={file}
                    setFile={setFile}
                    width={(typeof file === "object" && file?.width) || width}
                    height={(typeof file === "object" && file?.height) || height}
                    name={t("Image")}
                    canWrite={canWrite}
                />
                <ErrorMessage error={errors.image && t(errors.image)}/>
            </div>

            <br/>
            {
                canWrite && <ul className="tabs">
                    <li>
                        <a href="#" className="action-button" onClick={(e) => {
                            e.preventDefault();
                            e.currentTarget.closest('form').requestSubmit();
                        }}>
                            <FeatherIcon icon="check"/> {t("Save")}
                        </a>
                    </li>
                    {playerId && !hideDelete &&
                        <li>
                            <a href="#" className="action-button" onClick={handleDelete}>
                                <FeatherIcon icon="trash-2"/> {t("Delete player")}
                            </a>
                        </li>
                    }
                </ul>
            }

        </form>
    );
};

export default PlayerForm;
